import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Redirect, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {Button, Container, Form, Header, Loader, Message, Segment} from 'semantic-ui-react';

import {
  McpReplyDraft,
  cancelMcpReplyDraft,
  editMcpReplyDraft,
  getMcpReplyDraft,
  sendMcpReplyDraft
} from '../../services/mcpApi';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';
import {
  canOpenMcpIntegrationSettings,
  isMcpFeatureAvailable
} from './integrations';

type ReplyDraftViewProps = {
  currentUserId?: string;
  draft?: McpReplyDraft;
  loading: boolean;
  busy: boolean;
  error?: string;
  onSave: (text: string) => void;
  onCancel: () => void;
  onSend: (version: number) => void;
};

export const ReplyDraftApprovalView: React.FC<ReplyDraftViewProps> = ({
  currentUserId,
  draft,
  loading,
  busy,
  error,
  onSave,
  onCancel,
  onSend
}) => {
  const {t} = useTranslation();
  const [text, setText] = useState(draft?.text ?? '');
  useEffect(() => setText(draft?.text ?? ''), [draft?.text, draft?.version]);
  if (loading) return <Loader active inline="centered" />;
  const changed = Boolean(draft && text !== draft.text);
  const recipients =
    draft?.conversation.participants.filter(person => person.id !== currentUserId) ?? [];

  return (
    <Container text>
      <Segment padded>
        <Header as="h1">{t('McpReviewReply', 'Review reply')}</Header>
        {error && <Message negative content={error} />}
        {draft && (
          <>
            <p>
              <strong>{t('Conversation')}:</strong>{' '}
              <span>{draft.conversation.subject ?? t('Conversation')}</span>
            </p>
            <p>
              <strong>{t('McpTo', 'To')}:</strong>{' '}
              {recipients.map(person => person.displayName ?? person.id).join(', ')}
            </p>
            <Form>
              <Form.TextArea
                aria-label={t('McpReplyText', 'Reply text')}
                value={text}
                maxLength={20000}
                rows={8}
                disabled={busy || draft.status !== 'draft'}
                onChange={event => setText(event.currentTarget.value)}
              />
            </Form>
            {draft.status === 'sent' ? (
              <Message positive content={t('McpReplySent', 'Reply sent.')} />
            ) : draft.status === 'cancelled' ? (
              <Message content={t('McpReplyCancelled', 'Draft cancelled.')} />
            ) : (
              <>
                <Message info>
                  {t('McpNotSent', 'This message has not been sent.')}
                </Message>
                <Button
                  type="button"
                  disabled={busy}
                  content={t('Cancel')}
                  onClick={onCancel}
                />
                {changed && (
                  <Button
                    type="button"
                    disabled={busy || text.length === 0}
                    content={t('McpSaveChanges', 'Save changes')}
                    onClick={() => onSave(text)}
                  />
                )}
                <Button
                  type="button"
                  primary
                  disabled={busy || changed}
                  loading={busy}
                  content={t('McpSendReply', 'Send reply')}
                  onClick={() => {
                    if (
                      window.confirm(
                        t('McpSendConfirmation', 'Send this exact reply now?')
                      )
                    ) {
                      onSend(draft.version);
                    }
                  }}
                />
              </>
            )}
          </>
        )}
      </Segment>
    </Container>
  );
};

const ReplyDraftApproval: React.FC = () => {
  const {draftId} = useParams<{draftId: string}>();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const activeAccount = useSelector((state: any) => state.account.activeAccount);
  const {flags, isLoading: flagsLoading} = useFeatureFlags();
  const featureEnabled = isMcpFeatureAvailable(flags, flagsLoading, userProfile);
  const canOpenSettings =
    featureEnabled && canOpenMcpIntegrationSettings(userProfile, activeAccount);
  const [draft, setDraft] = useState<McpReplyDraft>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let current = true;
    if (!canOpenSettings) {
      setLoading(false);
      return () => {
        current = false;
      };
    }
    getMcpReplyDraft(draftId)
      .then(value => {
        if (current) setDraft(value);
      })
      .catch(reason => {
        if (current) setError(reason.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [canOpenSettings, draftId]);

  const perform = async (action: () => Promise<any>) => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await action();
      if (result) setDraft(result);
    } catch (reason: any) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  };

  if (flagsLoading || !userProfile) return <Loader active inline="centered" />;
  if (!canOpenSettings) return <Redirect to="/account/settings" />;

  return (
    <ReplyDraftApprovalView
      currentUserId={userProfile?._id}
      draft={draft}
      loading={loading}
      busy={busy}
      error={error}
      onSave={text => perform(() => editMcpReplyDraft(draftId, text))}
      onCancel={() =>
        perform(async () => {
          await cancelMcpReplyDraft(draftId);
          return draft ? {...draft, status: 'cancelled' as const} : undefined;
        })
      }
      onSend={version =>
        perform(async () => {
          await sendMcpReplyDraft(draftId, version);
          return draft ? {...draft, status: 'sent' as const} : undefined;
        })
      }
    />
  );
};

export default ReplyDraftApproval;
